import { doc, runTransaction } from "firebase/firestore";
import { db } from "@/services/db";
import { store } from "@/stores/useStore";
import clone from "just-clone";
import { Card, Container, Shop } from "@/types/v2";
import shortUUID from "short-uuid";
import { layout as layoutRepository } from "@/repositories/layout";
import { card as cardRepository } from "@/repositories/cards";
import {
  normalizeLabels,
  normalizeTechs,
} from "@/lib/checkContainerConfiguration";
import { isBulkSelectable } from "@/lib/containerSelection";
import { mergeLabelIntoContainer } from "@/lib/mergeContainerLabel";
import {
  LinkAdditions,
  mergeLinksIntoContainer,
  hasLinkRemovals,
  hasSyncOverrides,
  NO_SYNC_OVERRIDES,
  SyncOverrides,
} from "@/lib/mergeContainerLinks";

/**
 * Collapses a normalized bidirectional-label selection back to the narrowest
 * representation: a bare string for 0-1 labels (matching the format every
 * existing single-label config and consumer, e.g. resolveLabelSyncEvent,
 * already expects), and only an array once there are genuinely 2+.
 */
function collapseBidirectionalLabel(
  value: Container["bidirectionalLabel"]
): Container["bidirectionalLabel"] {
  const labels = normalizeLabels(value);
  return labels.length <= 1 ? (labels[0] ?? "") : labels;
}

/**
 * Collapses a normalized bidirectional-technician selection back to the
 * narrowest representation, mirroring collapseBidirectionalLabel exactly:
 * a bare string for 0-1 technicians, an array once there are genuinely 2+.
 */
function collapseBidirectionalTechnicianId(
  value: Container["bidirectionalTechnicianId"]
): Container["bidirectionalTechnicianId"] {
  const techs = normalizeTechs(value);
  return techs.length <= 1 ? (techs[0] ?? "") : techs;
}

export interface AddLinksResult {
  /** Eligible containers that were processed (idempotent: already-linked ones still count). */
  updatedIds: string[];
  /** The subset of `updatedIds` that actually changed (gained or lost a link, or had a sync value set); the rest already matched. */
  changedIds: string[];
  /** Ids that could not be updated: gone from the shop since selection, or dispatch. */
  skipped: { id: string; reason: "missing" | "dispatch" }[];
}

async function withContainersTransaction(
  shopId: string,
  mutate: (containers: Container[]) => boolean
): Promise<void> {
  const shopRef = doc(db, `shops/${shopId}`);
  await runTransaction(db, async (transaction) => {
    const shopSnap = await transaction.get(shopRef);
    const containers = clone(
      ((shopSnap.data() as Shop | undefined)?.containers ?? []) as Container[]
    );
    if (!mutate(containers)) return;
    transaction.update(shopRef, { containers });
  });
}

export const container = {
  updateContainer: (id: string, containerUpdate: Partial<Container>) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;

    return withContainersTransaction(shopId, (containers) => {
      const containerIndex = containers.findIndex((c) => c.id === id);
      if (containerIndex === -1) return false;
      // Only touch fields the caller actually provided (displayName,
      // linkedTechnicianId, linkedSourceLabel, bidirectionalLabel,
      // bidirectionalTechnicianId) — assigning `?? existing` when both sides
      // are undefined writes a literal `undefined` onto the container, which
      // Firestore's updateDoc rejects for the whole document.
      if (containerUpdate.displayName !== undefined) {
        containers[containerIndex].displayName = containerUpdate.displayName;
      }
      if (containerUpdate.linkedTechnicianId !== undefined) {
        containers[containerIndex].linkedTechnicianId =
          containerUpdate.linkedTechnicianId;
      }
      if (containerUpdate.linkedSourceLabel !== undefined) {
        containers[containerIndex].linkedSourceLabel =
          containerUpdate.linkedSourceLabel;
      }
      if (containerUpdate.bidirectionalLabel !== undefined) {
        containers[containerIndex].bidirectionalLabel =
          collapseBidirectionalLabel(containerUpdate.bidirectionalLabel);
      }
      if (containerUpdate.bidirectionalTechnicianId !== undefined) {
        containers[containerIndex].bidirectionalTechnicianId =
          collapseBidirectionalTechnicianId(
            containerUpdate.bidirectionalTechnicianId
          );
      }
      return true;
    });
  },
  /**
   * Adds `label` to each listed container's `linkedSourceLabel` in a single
   * transaction. The merge runs against the containers array read *inside*
   * the transaction (never a client snapshot), so a label another session
   * added a moment ago isn't overwritten, and the write is all-or-nothing.
   * Ids that no longer exist are skipped, and dispatch can never be linked
   * so it is skipped too. Resolves to how many containers were updated.
   */
  addLabelToContainers: async (ids: string[], label: string) => {
    const shopId = store.getState().shopId;
    if (!shopId) return 0;

    let updated = 0;
    await withContainersTransaction(shopId, (containers) => {
      // Firestore may re-run this callback on contention; count each attempt
      // from scratch so only the attempt that commits is reported.
      updated = 0;
      for (const target of containers) {
        if (!ids.includes(target.id) || !isBulkSelectable(target.id)) continue;
        target.linkedSourceLabel = mergeLabelIntoContainer(
          target.linkedSourceLabel,
          label
        );
        updated += 1;
      }
      return updated > 0;
    });
    return updated;
  },
  /**
   * Adds and removes technicians and labels on each listed container in ONE
   * transaction and at most one write. Every container lives in the single `containers` array on
   * the shop doc, so per-container writes would only contend on the same
   * document; the merge is still per container (see mergeLinksIntoContainer,
   * which also keeps new technician links two-way-sync OFF), run against the
   * array read *inside* the transaction so links another session added moments
   * ago are not overwritten. The write is all-or-nothing; "skipped" covers ids
   * that no longer exist and dispatch, which can never be linked.
   * Optional `sync` choices are absolute per-link on/off values (WEBV3-341) merged in the same transaction; a choice equal to the stored value writes nothing.
   * Removals (WEBV3-346) ride the same merge; the names are historical. A removal of a link a container no longer holds writes nothing and is not counted in changedIds.
   */
  addLinksToContainers: async (
    ids: string[],
    additions: LinkAdditions,
    sync: SyncOverrides = NO_SYNC_OVERRIDES
  ): Promise<AddLinksResult> => {
    const result: AddLinksResult = {
      updatedIds: [],
      changedIds: [],
      skipped: [],
    };
    const shopId = store.getState().shopId;
    if (!shopId) return result;
    if (
      additions.technicianIds.length === 0 &&
      additions.labels.length === 0 &&
      !hasLinkRemovals(additions) &&
      !hasSyncOverrides(sync)
    ) {
      return result;
    }

    const wanted = Array.from(new Set(ids));
    await withContainersTransaction(shopId, (containers) => {
      // Firestore may re-run this callback on contention; start from scratch
      // so only the attempt that commits is reported.
      result.updatedIds = [];
      result.changedIds = [];
      result.skipped = [];
      let changedAny = false;

      const existingIds = new Set(containers.map((c) => c.id));
      for (const id of wanted) {
        if (!isBulkSelectable(id)) {
          result.skipped.push({ id, reason: "dispatch" });
        } else if (!existingIds.has(id)) {
          result.skipped.push({ id, reason: "missing" });
        }
      }

      containers.forEach((target, index) => {
        if (!wanted.includes(target.id) || !isBulkSelectable(target.id)) return;
        const { container: merged, changed } = mergeLinksIntoContainer(
          target,
          additions,
          sync
        );
        result.updatedIds.push(target.id);
        if (changed) {
          result.changedIds.push(target.id);
          containers[index] = merged;
          changedAny = true;
        }
      });
      return changedAny;
    });
    return result;
  },
  updateContainerName: (id: string, displayName: string) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;

    return withContainersTransaction(shopId, (containers) => {
      const containerIndex = containers.findIndex((c) => c.id === id);
      if (containerIndex === -1) return false;
      containers[containerIndex].displayName = displayName;
      return true;
    });
  },

  updateContainerTechnician: (id: string, technicianId: string | string[]) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;

    return withContainersTransaction(shopId, (containers) => {
      const containerIndex = containers.findIndex((c) => c.id === id);
      if (containerIndex === -1) return false;
      const existing = containers[containerIndex];
      const previousLinkedIds = normalizeTechs(existing.linkedTechnicianId);
      const newLinkedIds = normalizeTechs(technicianId);
      existing.linkedTechnicianId = technicianId;
      if (existing.bidirectionalTechnicianId !== undefined) {
        // bidirectionalTechnicianId is already materialized (a real
        // subset), so a technician landing in linkedTechnicianId here for
        // the first time must default to bidirectional-enabled (opt-out),
        // the same default ContainerForm.tsx's handleAddTechnicians uses --
        // otherwise it would silently land outside the eligible set. Also
        // re-enforce the subset invariant so a technician dropped from
        // linkedTechnicianId here doesn't linger in bidirectionalTechnicianId.
        const addedIds = newLinkedIds.filter(
          (t) => !previousLinkedIds.includes(t)
        );
        const nextBidirectionalIds = Array.from(
          new Set([
            ...normalizeTechs(existing.bidirectionalTechnicianId),
            ...addedIds,
          ])
        ).filter((t) => newLinkedIds.includes(t));
        existing.bidirectionalTechnicianId =
          collapseBidirectionalTechnicianId(nextBidirectionalIds);
      }
      return true;
    });
  },
  deleteContainerTechnician: (id: string) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;

    return withContainersTransaction(shopId, (containers) => {
      const containerIndex = containers.findIndex((c) => c.id === id);
      if (containerIndex === -1) return false;
      delete containers[containerIndex].linkedTechnicianId;
      delete containers[containerIndex].bidirectionalTechnicianId;
      return true;
    });
  },

  deleteContainerLabel: (id: string) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;

    return withContainersTransaction(shopId, (containers) => {
      const containerIndex = containers.findIndex((c) => c.id === id);
      if (containerIndex === -1) return false;
      delete containers[containerIndex].linkedSourceLabel;
      delete containers[containerIndex].bidirectionalLabel;
      return true;
    });
  },
  createContainer: async (
    displayName: string,
    linkedTechnicianId: Container["linkedTechnicianId"] = [],
    linkedSourceLabel: Container["linkedSourceLabel"] = [],
    id?: string,
    bidirectionalTechnicianId?: Container["bidirectionalTechnicianId"]
  ) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;
    if (!id) {
      id = shortUUID.generate();
    }
    const newContainer: Container = {
      id,
      tags: [],
      displayName,
      linkedTechnicianId,
      linkedSourceLabel,
    };
    if (bidirectionalTechnicianId !== undefined) {
      newContainer.bidirectionalTechnicianId =
        collapseBidirectionalTechnicianId(bidirectionalTechnicianId);
    }

    await withContainersTransaction(shopId, (containers) => {
      containers.push(newContainer);
      return true;
    });
    return newContainer;
  },
  deleteContainer: async (id: string) => {
    const shopId = store.getState().shopId;
    if (!shopId) return;

    const layouts = layoutRepository.removeContainerFromAllLayouts(id);
    await layoutRepository.saveLayouts(shopId, layouts);

    await withContainersTransaction(shopId, (containers) => {
      const containerIndex = containers.findIndex((c) => c.id === id);
      if (containerIndex === -1) return false;
      containers.splice(containerIndex, 1);
      return true;
    });
    return;
  },
  getContainerName: (id: string) => {
    const containers = store.getState().shop?.containers ?? [];
    const container = containers.find((c) => c.id === id);
    return container?.displayName ?? id;
  },
  calculateContainerHours: (container: Container, cards: Card[]) => {
    const cardsInContainer = cards.filter((c) => container.tags.includes(c.id));
    const approvedHours = cardsInContainer.map((card) =>
      cardRepository.calculateApprovedHours(card)
    );
    const totalApprovedHours = approvedHours.reduce((a, b) => a + b, 0);
    const completedHours = cardsInContainer.map((card) =>
      cardRepository.calculateCompletedHours(card)
    );
    const totalCompletedHours = completedHours.reduce((a, b) => a + b, 0);
    return {
      totalApprovedHours,
      totalCompletedHours,
    };
  },
};
